import * as React from "react";
import { Paper, Typography } from "@mui/material";
import { EnvironmentState, IEnvironment } from "../../../types/EnvironmentTypes";
import { RefObject, useEffect, useRef, useState } from "react";
import { EnvironmentStateIndicator } from "./EnvironmentStateIndicator";

export interface EnvironmentBoxProps {
  environment: IEnvironment;
  initialPosition?: { x: number; y: number };
  hovered: boolean;
  onSizeChange?: (width: number, height: number) => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onDrag?: (x: number, y: number) => void;
  onClick?: (environment: IEnvironment) => void;
  stageRef?: RefObject<HTMLDivElement>;
}

export const EnvironmentBox = ({
  environment,
  initialPosition = { x: 0, y: 0 },
  hovered,
  onSizeChange,
  onMouseOver,
  onMouseOut,
  onDrag,
  onClick,
  stageRef,
}: EnvironmentBoxProps) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);
  const initialClientPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialDragPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [renderPosition, setRenderPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [renderSize, setRenderSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const moveDistance = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleClick = () => {
    if (onClick) {
      onClick(environment);
    }
  };

  const handleDocumentMouseMove = (e: MouseEvent) => {
    if (!boxRef.current) return;

    // Calculate cursor position relative to the SVG
    const rect = boxRef.current.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    // Only process if cursor is within or near the component bounds
    const margin = 20; // Extra pixels around the component to check
    if (cursorX >= -margin && cursorX <= rect.width + margin && cursorY >= -margin && cursorY <= rect.height + margin) {
      if (onMouseOver) {
        onMouseOver();
      }
    } else {
      if (hovered && onMouseOut) {
        onMouseOut();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleDocumentMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered]);

  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging.current || !stageRef?.current) return;

    // Calculate distance moved
    moveDistance.current = {
      x: Math.abs(e.clientX - initialClientPosition.current.x),
      y: Math.abs(e.clientY - initialClientPosition.current.y),
    };

    // Calculate new position relative to the stage
    const newX = initialDragPosition.current.x + (e.clientX - initialClientPosition.current.x);
    const newY = initialDragPosition.current.y + (e.clientY - initialClientPosition.current.y);

    dragPosition.current = { x: newX, y: newY };
    setRenderPosition({ x: newX, y: newY });

    if (onDrag) {
      onDrag(dragPosition.current.x, dragPosition.current.y);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();

    // Only consider left clicks
    if (e.button !== 0) return;

    isDragging.current = true;
    moveDistance.current = { x: 0, y: 0 };
    initialClientPosition.current = { x: e.clientX, y: e.clientY };
    initialDragPosition.current = dragPosition.current;

    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseUp = () => {
    const clickThreshold = 2;
    const wasClick = moveDistance.current.x <= clickThreshold && moveDistance.current.y <= clickThreshold;
    isDragging.current = false;

    // If barely moved (2 pixels), treat as a click
    if (wasClick && onClick) {
      handleClick();
    } else {
      if (onDrag) {
        onDrag(dragPosition.current.x, dragPosition.current.y);
      }
    }

    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  React.useEffect(() => {
    if (renderPosition.x === 0 && renderPosition.y === 0) {
      dragPosition.current = initialPosition;
      setRenderPosition(initialPosition);
    }
  }, [initialPosition, renderPosition]);

  React.useEffect(() => {
    const boxRect = boxRef.current?.getBoundingClientRect();
    const width = boxRect?.width ?? 220;
    const height = boxRect?.height ?? 64;
    if (width !== renderSize.width || height !== renderSize.height) {
      setRenderSize({ width, height });
      if (onSizeChange) {
        onSizeChange(width, height);
      }
    }
  }, [onSizeChange, renderSize]);

  return (
    <Paper
      ref={boxRef}
      elevation={3}
      sx={{
        width: "220px",
        padding: 2,
        margin: "8px",
        cursor: "move",
        position: "absolute",
        left: `${renderPosition.x}px`,
        top: `${renderPosition.y}px`,
        zIndex: isDragging.current ? 1000 : 1,
        transition: isDragging.current ? "none" : "box-shadow 0.3s ease",
        boxShadow: isDragging.current ? "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)" : undefined,
        "&:hover": {
          boxShadow: "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
        },
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
    >
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "row" }}>
        <Typography variant="subtitle1" fontWeight="bold" style={{ textAlign: "center" }}>
          {environment.name}
        </Typography>
        <EnvironmentStateIndicator state={environment.state || EnvironmentState.UNKNOWN} showLabel={false} />
      </div>
    </Paper>
  );
};
